/**
 * Small, pre-made copies of portraits for list and avatar sizes.
 *
 * Portraits are stored as 1024x1024 PNGs (~1.4 MB each) and were shown at
 * 32-112 px: one /author-matrix page pulled ~28 MB to draw 36 px circles, and
 * Mobile Safari's decoded-image memory cap dropped some outright.
 *
 * Every portrait has a 256x256 JPEG sibling at `<bucket>/thumbs/256/<name>.jpg`
 * (generated 2026-09-26 by a square downscale of the SAME image, so the crop is
 * identical; 508 MB -> 7.9 MB for all 363). The Supabase render/image transform
 * is not an option: it is not enabled here (403), and when tried before it
 * re-cropped the portraits.
 *
 * Nothing in the database changes -- `protagonist_portrait_url`, which the iOS
 * app reads, still points at the original. A portrait made after 2026-09-26 has
 * no thumb until one is generated, so callers MUST fall back to the original on
 * error (`portraitFallback`); the portrait generators delete a stale thumb when
 * they overwrite a portrait, which makes the same fallback cover regeneration.
 */
export const THUMB_SIZE = 256;

const PORTRAIT = /^(.*\/storage\/v1\/object\/public\/)(protagonist-portraits|author-portraits)\/([^/?#]+)\.png(?:[?#].*)?$/;

export function portraitThumbUrl(
  url: string | null | undefined,
  width = THUMB_SIZE,
): string | null {
  if (!url) return null;
  if (width > THUMB_SIZE) return url; // focal views keep the full-size original
  const m = url.match(PORTRAIT);
  if (!m) return url;
  return `${m[1]}${m[2]}/thumbs/${THUMB_SIZE}/${m[3]}.jpg`;
}

/** onError for an <img> showing a thumb: swap to the original, once. */
export function portraitFallback(original: string | null | undefined) {
  return (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (original && img.src !== original) img.src = original;
  };
}
